import { useState, useRef, useEffect } from 'react'

import type { MissionComponentProps } from './components'
import './atlasLab.css'

// === paleta (literal do hypatia-camadas.jsx) ===
const HC = {
  bg: '#000000',
  id: '#2a2a2a', idBorder: '#555',
  ecal: '#6abf4b', ecalDark: '#4a9a30',
  hcal: '#d4726a', hcalDark: '#b85550',
  muon: '#5b8ec9', muonDark: '#3a6a9f',
  tower: '#dddd00', met: '#ff2222', ip: '#ffffff',
}

// === extensão semântica (eletromagnetismo) ===
const PT_COLOR = '#f2a623'   // âmbar — momento transverso
const PZ_COLOR = '#5b8ec9'   // azul múons — momento longitudinal (axial)
const B_COLOR  = '#a78bfa'   // lilás — campo magnético
const POS_COLOR = '#e85d5d'  // partícula positiva
const NEG_COLOR = '#5b8ec9'  // partícula negativa
const SUCCESS = '#58cc02'

// === canvas dims ===
const CW = 620, CH = 440

// === fases ===
interface Phase {
  id: number
  tag: string
  title: string
  color: string
  what: string
  role: string
  detail: string
}

const PHASES: Phase[] = [
  {
    id: 0, tag: 'GEOMETRIA',
    title: 'O sistema de coordenadas',
    color: '#888',
    what: 'O ATLAS é cilíndrico. O eixo z atravessa o detector ao longo do feixe; o plano xy é perpendicular ao feixe.',
    role: 'Antes de qualquer física, fixar o referencial. Tudo o que vem depois — campo, momento, projeções — é relativo a este eixo.',
    detail: 'z = feixe. plano transverso = xy. Esta é a convenção universal em colisores.'
  },
  {
    id: 1, tag: 'ANTES',
    title: 'Antes da colisão',
    color: PZ_COLOR,
    what: 'Dois prótons viajam ao longo do eixo z, em sentidos opostos, com momentos longitudinais p_z de módulos iguais.',
    role: 'Estabelecer o estado inicial. Os feixes têm apenas p_z — não há momento no plano transverso, então Σp_T = 0.',
    detail: 'Conservação de momento aplicada a este estado é o que justifica que a soma de p_T no estado final também tenha que ser zero. Lembre-se disto na última fase.'
  },
  {
    id: 2, tag: 'COLISÃO',
    title: 'A colisão e o que emerge',
    color: '#fff',
    what: 'Quando dois pártons (um de cada próton) interagem, várias partículas finais emergem do vértice em direções variadas.',
    role: 'O processo duro redistribui momento. Algumas partículas saem para frente (p_z alto), outras transversalmente (p_T alto). As com p_T alto são as interessantes — sinal de transferência de momento substancial.',
    detail: 'Note as diferentes assinaturas: cargadas (vermelho/azul) seguirão hélices no campo magnético; γ fóton e ν neutrino não, por motivos distintos. Vamos seguir uma partícula carregada com massa — μ⁻, destacada pelo anel.'
  },
  {
    id: 3, tag: 'SEM CAMPO',
    title: 'Sem campo magnético',
    color: '#888',
    what: 'A partícula que escolhemos seguir, em um cenário hipotético sem campo magnético, atravessaria o detector em linha reta.',
    role: 'Caso-controle. Sem comparar com este caso, não fica claro que a curvatura é causada pelo campo — alunos tendem a achar que partículas \'simplesmente fazem curva no detector\'.',
    detail: 'A reta é o estado natural. A curva é o que precisa de explicação — e o ATLAS tem 2 T para isso.'
  },
  {
    id: 4, tag: 'CAMPO + p_T',
    title: 'Liga o campo: a hélice nasce',
    color: B_COLOR,
    what: 'O solenoide gera B ≈ 2 T paralelo a z. A força de Lorentz curva a partícula. O momento se decompõe em p_z (paralelo a B, constante) e p_T (perpendicular a B, módulo constante mas direção rotacionando).',
    role: 'A componente paralela ao campo passa imune; só a componente perpendicular sente a força e gira em círculo. O vetor azul (p_z) não muda; o laranja (p_T) mantém o módulo mas rotaciona.',
    detail: 'Inverta a carga: a hélice gira ao contrário. Zere o campo: volta a ser reta. Mude θ: muda a proporção entre avanço em z e raio do círculo.'
  },
  {
    id: 5, tag: 'PROJEÇÃO',
    title: 'Onde se mede p_T',
    color: PT_COLOR,
    what: 'A vista transversa do HYPATIA é a projeção da hélice no plano xy — vira um círculo de raio R.',
    role: 'A fórmula p_T [GeV] = 0,3 · B [T] · R [m] permite ler o momento transverso direto da curvatura do traço no detector.',
    detail: 'Casos-limite: θ → 0° faz R → ∞ (reta no plano transverso). θ → 90° faz a partícula girar sem avançar em z.'
  },
  {
    id: 6, tag: 'SOMA VETORIAL',
    title: 'Conservação no plano transverso',
    color: HC.met,
    what: 'Lembra que na Fase 1 a soma Σp_T era zero? Conservação impõe que a soma sobre todas as partículas finais também seja zero.',
    role: 'Qualquer desbalanço é energia faltante (MET): neutrino, ou algo desconhecido escapou. Cada partícula individual tem seu p_T não-nulo; o que se anula é a soma vetorial.',
    detail: 'Esta é a razão de toda a comunidade do LHC trabalhar em variáveis transversas — só elas fecham, então qualquer \'buraco\' aponta para uma descoberta possível.'
  },
]

// === partículas da Fase 2 (direções fixas para reprodutibilidade em aula) ===
interface CollisionPart {
  theta: number
  phi: number
  color: string
  label: string
  dashed: boolean
  highlighted: boolean
}

const COLLISION_PARTS: CollisionPart[] = [
  { theta: 70, phi: 30, color: POS_COLOR, label: 'μ⁺', dashed: false, highlighted: false },
  { theta: 55, phi: 160, color: NEG_COLOR, label: 'μ⁻', dashed: false, highlighted: true },
  { theta: 110, phi: 200, color: POS_COLOR, label: 'e⁺', dashed: false, highlighted: false },
  { theta: 130, phi: 80, color: NEG_COLOR, label: 'e⁻', dashed: false, highlighted: false },
  { theta: 85, phi: 270, color: HC.tower, label: 'γ', dashed: false, highlighted: false },
  { theta: 60, phi: 320, color: '#666', label: 'ν', dashed: true, highlighted: false },
]

// === projeção isométrica manual ===
// roda em torno de y (azimute beta), depois em torno de x (elevação alpha)
function project3D(x: number, y: number, z: number, alpha: number, beta: number): { x: number; y: number } {
  const ca = Math.cos(alpha), sa = Math.sin(alpha)
  const cb = Math.cos(beta), sb = Math.sin(beta)
  const x1 = x * cb + z * sb
  const z1 = -x * sb + z * cb
  const y2 = y * ca - z1 * sa
  return { x: x1, y: y2 }
}

// === helper para raio físico ===
function helixRadius(pT_GeV: number, B_T: number): number {
  // R [m] = pT / (0.3 * B), saturação para evitar /0
  return pT_GeV / (0.3 * Math.max(B_T, 0.001))
}

const MISSION_ID = 'atlas.hypatia.momentoTransverso'

export function HypatiaMomentoTransverso(props: MissionComponentProps) {
  const { currentActivityId, onLayerFocused, onMissionEvent, readOnly = false } = props

  const cvRef = useRef<HTMLCanvasElement | null>(null)
  const tRef = useRef<number>(0)
  const rafRef = useRef<number | null>(null)
  const lastTime = useRef<number | null>(null)

  // === telemetria (mesmo padrão do HypatiaTutorial) ===
  const onMissionEventRef = useRef(onMissionEvent)
  onMissionEventRef.current = onMissionEvent
  const enteredOnceRef = useRef(false)
  const phaseRef = useRef(0)
  const phaseEnteredAtRef = useRef<number>(Date.now())
  const missionEnteredAtRef = useRef<number>(Date.now())
  const completedFiredRef = useRef(false)

  const [phase, setPhase] = useState(0)
  const [visited, setVisited] = useState<Record<number, boolean>>({ 0: true })
  const [B, setB] = useState(2.0)
  const [theta, setTheta] = useState(55)
  const [pTotal, setPTotal] = useState(15)
  const [charge, setCharge] = useState(1)
  const [showGhost] = useState(true)
  const [withNeutrino, setWithNeutrino] = useState(false)
  const [autoRotate, setAutoRotate] = useState(true)
  const [isDragging, setIsDragging] = useState(false)

  // câmera: azimute (rotação em torno de y) e elevação (rotação em torno de x)
  const camRef = useRef<{ az: number; el: number }>({ az: 0, el: 22 * Math.PI / 180 })
  const dragRef = useRef<{ active: boolean; startX: number; startY: number; startAz: number; startEl: number }>({ active: false, startX: 0, startY: 0, startAz: 0, startEl: 0 })

  const ph = PHASES[phase]
  // Cor de acento da fase para a chrome no fundo CLARO. A fase "colisão" usa
  // '#fff' (legível só no canvas escuro) — cai pra ink na chrome.
  const cardAccent = ph.color === '#fff' ? 'var(--color-lab-ink, #0F1115)' : ph.color
  const allVisited = PHASES.every(p => visited[p.id])

  // === valores derivados ===
  const thetaRad = theta * Math.PI / 180
  const pT = pTotal * Math.sin(thetaRad)
  const pZ = pTotal * Math.cos(thetaRad)
  const R_m = helixRadius(pT, B)
  const SCALE_PX_PER_M = 2.7 // escala visual
  const R_px = R_m * SCALE_PX_PER_M

  // === controle de câmera via pointer (mouse + touch) ===
  const onPointerDown = (e: React.MouseEvent | React.TouchEvent) => {
    // só permite drag em painéis 3D — fase 6 é a vista MET 2D
    if (phase === 6) return
    const pt = 'touches' in e ? e.touches[0] : e
    dragRef.current.active = true
    dragRef.current.startX = pt.clientX
    dragRef.current.startY = pt.clientY
    dragRef.current.startAz = camRef.current.az
    dragRef.current.startEl = camRef.current.el
    setIsDragging(true)
    setAutoRotate(false) // pausa auto-rotação ao tocar
  }
  const onPointerMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!dragRef.current.active) return
    e.preventDefault()
    const pt = 'touches' in e ? e.touches[0] : e
    const dx = pt.clientX - dragRef.current.startX
    const dy = pt.clientY - dragRef.current.startY
    const sens = 0.008 // rad/px
    camRef.current.az = dragRef.current.startAz + dx * sens
    // elevação limitada para evitar gimbal lock visual
    const newEl = dragRef.current.startEl + dy * sens
    camRef.current.el = Math.max(-Math.PI / 2.2, Math.min(Math.PI / 2.2, newEl))
  }
  const onPointerUp = () => {
    if (!dragRef.current.active) return
    dragRef.current.active = false
    setIsDragging(false)
  }

  // === avança para próxima fase visitada ===
  const goPhase = (i: number, trigger: 'click' | 'auto' = 'click', source: 'self' | 'master-propagation' = 'self') => {
    const from = phaseRef.current
    const now = Date.now()
    onMissionEventRef.current?.('phaseAdvanced', {
      fromPhase: from,
      toPhase: i,
      trigger,
      dwellInPreviousPhaseMs: now - phaseEnteredAtRef.current,
      source,
      clientTs: now,
    })
    phaseRef.current = i
    phaseEnteredAtRef.current = now
    setPhase(i)
    setVisited(v => ({ ...v, [i]: true }))
    // reset do relógio para a hélice nascer no vértice (idem prototype, via effect [phase])
    tRef.current = 0
  }

  // === missionEntered: dispara uma vez no mount ===
  useEffect(() => {
    if (enteredOnceRef.current) return
    enteredOnceRef.current = true
    const now = Date.now()
    phaseEnteredAtRef.current = now
    missionEnteredAtRef.current = now
    onMissionEventRef.current?.('missionEntered', { clientTs: now })
  }, [])

  // === missionCompleted: dispara uma vez quando todas as fases foram visitadas ===
  useEffect(() => {
    if (!allVisited) return
    if (completedFiredRef.current) return
    completedFiredRef.current = true
    const now = Date.now()
    onMissionEventRef.current?.('missionCompleted', {
      phasesVisited: PHASES.length,
      totalDwellMs: now - missionEnteredAtRef.current,
      clientTs: now,
    })
  }, [allVisited])

  // === sync conduzido pelo mestre: ${MISSION_ID}.phase-{N} ===
  useEffect(() => {
    if (!currentActivityId) return
    const prefix = `${MISSION_ID}.`
    if (!currentActivityId.startsWith(prefix)) return
    const sub = currentActivityId.slice(prefix.length)
    if (!sub.startsWith('phase-')) return
    const n = parseInt(sub.slice('phase-'.length), 10)
    if (!Number.isNaN(n) && n >= 0 && n < PHASES.length && n !== phaseRef.current) {
      goPhase(n, 'click', 'master-propagation')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentActivityId])

  // === reset do relógio ao mudar de fase: garante hélice sempre visível desde o vértice ===
  useEffect(() => {
    tRef.current = 0
    lastTime.current = null
  }, [phase])

  // === loop de animação ===
  useEffect(() => {
    const cv = cvRef.current; if (!cv) return
    const ctx = cv.getContext('2d')
    if (!ctx) return
    const dpr = window.devicePixelRatio || 1
    cv.width = CW * dpr; cv.height = CH * dpr
    // Tamanho de exibição vem do frame (.atlas-lab-canvas = 100%); o bitmap
    // fica em CW×CH×dpr e o CSS escala pra preencher o card sem sobrar fundo.
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

    const tick = (time: number) => {
      if (lastTime.current == null) lastTime.current = time
      const dt = (time - lastTime.current) / 1000
      lastTime.current = time
      tRef.current += dt
      // auto-rotação da câmera (apenas quando habilitada e usuário não está arrastando)
      if (autoRotate && !dragRef.current.active) {
        camRef.current.az += 0.08 * dt
      }
      draw(ctx)
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
      lastTime.current = null
    }
  }, [phase, B, theta, pTotal, charge, showGhost, withNeutrino, autoRotate])

  // === desenho principal ===
  function draw(ctx: CanvasRenderingContext2D) {
    ctx.fillStyle = HC.bg
    ctx.fillRect(0, 0, CW, CH)

    if (phase === 6) {
      drawMETScene(ctx)
      return
    }
    if (phase === 1) {
      drawBeforeCollisionScene(ctx, 0, 0, CW, CH)
      return
    }
    if (phase === 2) {
      drawCollisionScene(ctx, 0, 0, CW, CH)
      return
    }

    // Fases 0, 3, 4, 5: painéis 3D (eventualmente com vista transversa em 5)
    const dual = phase === 5
    if (dual) {
      draw3DPanel(ctx, 0, 0, 340, CH)
      drawTransversePanel(ctx, 340, 0, 280, CH)
    } else {
      draw3DPanel(ctx, 0, 0, CW, CH)
    }
  }

  // === painel 3D ===
  function draw3DPanel(ctx: CanvasRenderingContext2D, ox: number, oy: number, w: number, h: number) {
    const cx = ox + w / 2, cy = oy + h / 2
    const alpha = camRef.current.el
    const beta = camRef.current.az

    // cilindro do detector wireframe
    drawDetectorWireframe(ctx, cx, cy, alpha, beta)

    // setas de B (apenas a partir da fase 4 — CAMPO LIGADO)
    if (phase >= 4) drawBFieldArrows(ctx, cx, cy, alpha, beta)

    // eixos gizmo no canto inferior esquerdo
    drawAxisGizmo(ctx, ox + 50, oy + h - 50, alpha, beta)

    // partícula + trajetória (fases 3, 4, 5 — acompanhando uma partícula)
    if (phase >= 3 && phase <= 5) {
      drawTrajectory3D(ctx, cx, cy, alpha, beta)
    }

    // vértice IP
    if (phase >= 3) {
      const { x: ipx, y: ipy } = project3D(0, 0, 0, alpha, beta)
      ctx.fillStyle = '#fff'
      ctx.beginPath(); ctx.arc(cx + ipx, cy + ipy, 3, 0, Math.PI * 2); ctx.fill()
      ctx.fillStyle = 'rgba(255,255,255,0.15)'
      ctx.beginPath(); ctx.arc(cx + ipx, cy + ipy, 8, 0, Math.PI * 2); ctx.fill()
    }

    // legendas de fase 0
    if (phase === 0) {
      ctx.fillStyle = 'rgba(255,255,255,0.18)'
      ctx.font = "600 11px 'Inter Tight',sans-serif"
      ctx.textAlign = 'center'
      ctx.fillText('Detector ATLAS', cx, oy + 30)
      ctx.fillText('cilíndrico em torno do eixo z', cx, oy + 46)
    }
  }

  function drawDetectorWireframe(ctx: CanvasRenderingContext2D, cx: number, cy: number, alpha: number, beta: number) {
    const R = 140 // raio do detector em "unidades"
    const Z = 220 // meio-comprimento em z

    // anéis de extremidade
    ctx.strokeStyle = '#1a1a1a'
    ctx.lineWidth = 0.8
    for (const zEnd of [-Z, 0, Z]) {
      ctx.beginPath()
      for (let i = 0; i <= 64; i++) {
        const a = i / 64 * Math.PI * 2
        const p = project3D(R * Math.cos(a), R * Math.sin(a), zEnd, alpha, beta)
        if (i === 0) ctx.moveTo(cx + p.x, cy + p.y)
        else ctx.lineTo(cx + p.x, cy + p.y)
      }
      ctx.stroke()
    }

    // geratrizes
    for (let i = 0; i < 8; i++) {
      const a = i / 8 * Math.PI * 2
      const p1 = project3D(R * Math.cos(a), R * Math.sin(a), -Z, alpha, beta)
      const p2 = project3D(R * Math.cos(a), R * Math.sin(a), Z, alpha, beta)
      ctx.beginPath()
      ctx.moveTo(cx + p1.x, cy + p1.y)
      ctx.lineTo(cx + p2.x, cy + p2.y)
      ctx.stroke()
    }

    // eixo z (feixe)
    ctx.strokeStyle = '#2a2a2a'
    ctx.lineWidth = 1.2
    const z1 = project3D(0, 0, -Z - 30, alpha, beta)
    const z2 = project3D(0, 0, Z + 30, alpha, beta)
    ctx.beginPath()
    ctx.moveTo(cx + z1.x, cy + z1.y)
    ctx.lineTo(cx + z2.x, cy + z2.y)
    ctx.stroke()
    // rótulo z
    ctx.fillStyle = 'rgba(255,255,255,0.35)'
    ctx.font = "italic 600 13px 'Inter Tight',sans-serif"
    ctx.textAlign = 'left'
    ctx.fillText('z', cx + z2.x + 4, cy + z2.y + 4)
  }

  function drawBFieldArrows(ctx: CanvasRenderingContext2D, cx: number, cy: number, alpha: number, beta: number) {
    const intensity = Math.min(B / 2.0, 1)
    const arrowColor = `rgba(167, 139, 250, ${0.15 + intensity * 0.5})`
    const positions: { x: number; y: number }[] = []
    // distribuir em uma malha pelo volume
    for (const r of [40, 90]) {
      for (let i = 0; i < 6; i++) {
        const a = i / 6 * Math.PI * 2
        positions.push({ x: r * Math.cos(a), y: r * Math.sin(a) })
      }
    }
    for (const p of positions) {
      const z1 = -40, z2 = 40
      const a = project3D(p.x, p.y, z1, alpha, beta)
      const b = project3D(p.x, p.y, z2, alpha, beta)
      ctx.strokeStyle = arrowColor
      ctx.lineWidth = 1.2
      ctx.beginPath()
      ctx.moveTo(cx + a.x, cy + a.y)
      ctx.lineTo(cx + b.x, cy + b.y)
      ctx.stroke()
      // cabeça da seta
      const dx = b.x - a.x, dy = b.y - a.y
      const len = Math.sqrt(dx * dx + dy * dy)
      if (len > 0.1) {
        const ux = dx / len, uy = dy / len
        const px = -uy, py = ux
        ctx.beginPath()
        ctx.moveTo(cx + b.x, cy + b.y)
        ctx.lineTo(cx + b.x - 5 * ux + 3 * px, cy + b.y - 5 * uy + 3 * py)
        ctx.lineTo(cx + b.x - 5 * ux - 3 * px, cy + b.y - 5 * uy - 3 * py)
        ctx.closePath()
        ctx.fillStyle = arrowColor
        ctx.fill()
      }
    }
    // rótulo B no canto
    ctx.fillStyle = `rgba(167, 139, 250, ${0.5 + intensity * 0.4})`
    ctx.font = "italic 700 13px 'Inter Tight',sans-serif"
    ctx.textAlign = 'left'
    ctx.fillText(`B = ${B.toFixed(1)} T`, 14, 28)
  }

  function drawAxisGizmo(ctx: CanvasRenderingContext2D, cx: number, cy: number, alpha: number, beta: number) {
    const L = 22
    const axes = [
      { dx: L, dy: 0, dz: 0, col: '#888', lbl: 'x' },
      { dx: 0, dy: L, dz: 0, col: '#888', lbl: 'y' },
      { dx: 0, dy: 0, dz: L, col: '#aaa', lbl: 'z' },
    ]
    for (const ax of axes) {
      const p = project3D(ax.dx, ax.dy, ax.dz, alpha, beta)
      ctx.strokeStyle = ax.col
      ctx.lineWidth = 1.3
      ctx.beginPath()
      ctx.moveTo(cx, cy)
      ctx.lineTo(cx + p.x, cy + p.y)
      ctx.stroke()
      ctx.fillStyle = ax.col
      ctx.font = "italic 600 11px 'Inter Tight',sans-serif"
      ctx.fillText(ax.lbl, cx + p.x * 1.2, cy + p.y * 1.2 + 3)
    }
  }

  // === trajetória 3D ===
  function drawTrajectory3D(ctx: CanvasRenderingContext2D, cx: number, cy: number, alpha: number, beta: number) {
    const partColor = charge > 0 ? POS_COLOR : NEG_COLOR
    // tempo visual: 1 volta a cada 2.5s para um pT de referência
    const refOmega = 2 * Math.PI / 2.5
    // omega sempre positivo — sentido vem de angleSign
    const omega = refOmega * Math.max(B / 2.0, 0.3)
    const angleSign = charge > 0 ? 1 : -1
    // pitch (avanço por radiano)
    const pitch = (pZ / Math.max(pT, 0.5)) * 8

    // ghost (reta sem campo) — fase 4 (CAMPO + p_T), para comparar com a hélice
    if (showGhost && phase === 4) {
      drawStraightGhost(ctx, cx, cy, alpha, beta)
    }

    // se B for praticamente zero ou pT zero, traja é reta
    const isStraight = phase === 3 || B < 0.05 || pT < 0.3

    if (isStraight) {
      drawStraightTrack(ctx, cx, cy, alpha, beta, partColor)
      return
    }

    // ciclo: a partícula "renasce" no vértice quando atinge a borda do detector em z
    const Z_MAX = 240
    const sCycleMax = pitch > 0.1 ? Z_MAX / pitch : 12 * Math.PI // se p_z baixo, cicla por número de voltas
    const sTotal = tRef.current * omega
    const sMax = sTotal % sCycleMax
    // sMin não pode retroceder antes do vértice (s=0)
    const sMin = Math.max(0, sMax - 6 * Math.PI)
    const useR = Math.min(R_px, 280)

    const segments = 200
    for (let i = 0; i < segments; i++) {
      const f1 = i / segments, f2 = (i + 1) / segments
      const s1 = sMin + (sMax - sMin) * f1
      const s2 = sMin + (sMax - sMin) * f2
      const x1 = useR * Math.cos(s1 * angleSign), y1 = useR * Math.sin(s1 * angleSign), z1 = s1 * pitch
      const x2 = useR * Math.cos(s2 * angleSign), y2 = useR * Math.sin(s2 * angleSign), z2 = s2 * pitch
      const p1 = project3D(x1, y1, z1, alpha, beta)
      const p2 = project3D(x2, y2, z2, alpha, beta)
      const alphaTrail = Math.pow(f1, 1.6) // mais transparente atrás
      ctx.strokeStyle = partColor
      ctx.globalAlpha = alphaTrail
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.moveTo(cx + p1.x, cy + p1.y)
      ctx.lineTo(cx + p2.x, cy + p2.y)
      ctx.stroke()
    }
    ctx.globalAlpha = 1

    // posição atual (cabeça)
    const sNow = sMax
    const xN = useR * Math.cos(sNow * angleSign), yN = useR * Math.sin(sNow * angleSign), zN = sNow * pitch
    const pN = project3D(xN, yN, zN, alpha, beta)
    ctx.fillStyle = partColor
    ctx.beginPath()
    ctx.arc(cx + pN.x, cy + pN.y, 5, 0, Math.PI * 2)
    ctx.fill()
    ctx.shadowColor = partColor
    ctx.shadowBlur = 8
    ctx.beginPath()
    ctx.arc(cx + pN.x, cy + pN.y, 3, 0, Math.PI * 2)
    ctx.fill()
    ctx.shadowBlur = 0

    // vetores p_z e p_T grudados na partícula — fases 4 e 5
    if (phase === 4 || phase === 5) {
      drawMomentumVectors(ctx, cx, cy, alpha, beta, xN, yN, zN, sNow)
    }
  }

  function drawStraightGhost(ctx: CanvasRenderingContext2D, cx: number, cy: number, alpha: number, beta: number) {
    // reta a 45° do plano xy, ao longo do que seria a partícula sem campo
    const dirX = Math.sin(thetaRad) * Math.cos(0)
    const dirY = Math.sin(thetaRad) * Math.sin(0)
    const dirZ = Math.cos(thetaRad)
    const L = 260
    const p1 = project3D(0, 0, 0, alpha, beta)
    const p2 = project3D(L * dirX, L * dirY, L * dirZ, alpha, beta)
    ctx.strokeStyle = 'rgba(120,120,120,0.35)'
    ctx.setLineDash([3, 3])
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(cx + p1.x, cy + p1.y)
    ctx.lineTo(cx + p2.x, cy + p2.y)
    ctx.stroke()
    ctx.setLineDash([])
  }

  function drawStraightTrack(ctx: CanvasRenderingContext2D, cx: number, cy: number, alpha: number, beta: number, color: string) {
    const dirX = Math.sin(thetaRad) * Math.cos(0)
    const dirY = Math.sin(thetaRad) * Math.sin(0)
    const dirZ = Math.cos(thetaRad)
    // a partícula se move ao longo dessa direção
    const speed = 60 // px/s
    const dist = (tRef.current * speed) % 280
    const segments = 60
    for (let i = 0; i < segments; i++) {
      const f1 = i / segments, f2 = (i + 1) / segments
      const d1 = Math.max(0, dist - 100 * (1 - f1))
      const d2 = Math.max(0, dist - 100 * (1 - f2))
      const p1 = project3D(d1 * dirX, d1 * dirY, d1 * dirZ, alpha, beta)
      const p2 = project3D(d2 * dirX, d2 * dirY, d2 * dirZ, alpha, beta)
      ctx.strokeStyle = color
      ctx.globalAlpha = f1
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.moveTo(cx + p1.x, cy + p1.y)
      ctx.lineTo(cx + p2.x, cy + p2.y)
      ctx.stroke()
    }
    ctx.globalAlpha = 1
    // cabeça
    const ph_ = project3D(dist * dirX, dist * dirY, dist * dirZ, alpha, beta)
    ctx.fillStyle = color
    ctx.shadowColor = color
    ctx.shadowBlur = 8
    ctx.beginPath()
    ctx.arc(cx + ph_.x, cy + ph_.y, 5, 0, Math.PI * 2)
    ctx.fill()
    ctx.shadowBlur = 0
  }

  function drawMomentumVectors(ctx: CanvasRenderingContext2D, cx: number, cy: number, alpha: number, beta: number, xN: number, yN: number, zN: number, sNow: number) {
    // p_z vetor: do ponto da partícula, paralelo a z, comprimento proporcional a |p_z|
    const lenZ = pZ * 3.5
    const pa = project3D(xN, yN, zN, alpha, beta)
    const pb = project3D(xN, yN, zN + lenZ, alpha, beta)
    drawArrow(ctx, cx + pa.x, cy + pa.y, cx + pb.x, cy + pb.y, PZ_COLOR, 2.5)
    ctx.fillStyle = PZ_COLOR
    ctx.font = "italic 700 12px 'Inter Tight',sans-serif"
    ctx.textAlign = 'left'
    ctx.fillText('p_z', cx + pb.x + 6, cy + pb.y)

    // p_T vetor: tangente ao círculo no plano xy, calculada diretamente da posição.
    // Rotação de 90° anti-horária da posição radial dá a tangente correta para giro positivo;
    // multiplicar por angleSign inverte para carga negativa.
    const angleSign = charge > 0 ? 1 : -1
    const rXY = Math.sqrt(xN * xN + yN * yN)
    const tx = rXY > 0.1 ? (-yN / rXY) * angleSign : 0
    const ty = rXY > 0.1 ? (xN / rXY) * angleSign : 0
    const lenT = pT * 3.5
    const tEnd = project3D(xN + tx * lenT, yN + ty * lenT, zN, alpha, beta)
    // pulso a cada 1/4 de volta
    const pulse = 1 + 0.3 * Math.max(0, Math.cos(sNow * 4))
    drawArrow(ctx, cx + pa.x, cy + pa.y, cx + tEnd.x, cy + tEnd.y, PT_COLOR, 2.5 * pulse)
    ctx.fillStyle = PT_COLOR
    ctx.font = "italic 700 12px 'Inter Tight',sans-serif"
    ctx.fillText('p_T', cx + tEnd.x + 6, cy + tEnd.y)
  }

  function drawArrow(ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number, color: string, lw: number) {
    ctx.strokeStyle = color
    ctx.fillStyle = color
    ctx.lineWidth = lw
    ctx.beginPath()
    ctx.moveTo(x1, y1)
    ctx.lineTo(x2, y2)
    ctx.stroke()
    const dx = x2 - x1, dy = y2 - y1
    const len = Math.sqrt(dx * dx + dy * dy)
    if (len < 1) return
    const ux = dx / len, uy = dy / len
    const px = -uy, py = ux
    const head = 7
    ctx.beginPath()
    ctx.moveTo(x2, y2)
    ctx.lineTo(x2 - head * ux + (head * 0.5) * px, y2 - head * uy + (head * 0.5) * py)
    ctx.lineTo(x2 - head * ux - (head * 0.5) * px, y2 - head * uy - (head * 0.5) * py)
    ctx.closePath()
    ctx.fill()
  }

  // === Fase 1: antes da colisão (dois prótons aproximando-se) ===
  function drawBeforeCollisionScene(ctx: CanvasRenderingContext2D, ox: number, oy: number, w: number, h: number) {
    const cx = ox + w / 2, cy = oy + h / 2
    const alpha = camRef.current.el
    const beta = camRef.current.az

    drawDetectorWireframe(ctx, cx, cy, alpha, beta)
    drawAxisGizmo(ctx, ox + 50, oy + h - 50, alpha, beta)

    // ciclo de aproximação + colisão + pausa
    const T = 4.0
    const t = tRef.current % T
    const tApproach = 2.8
    const tFlash = 0.4

    if (t < tApproach) {
      // posição dos prótons interpolada linearmente
      const frac = t / tApproach
      const zMax = 230
      const zCurrent = zMax * (1 - frac)
      // próton positivo viaja de +z para o IP (momento em -z)
      drawProton(ctx, cx, cy, alpha, beta, 0, 0, +zCurrent, -1)
      // próton negativo viaja de -z para o IP (momento em +z)
      drawProton(ctx, cx, cy, alpha, beta, 0, 0, -zCurrent, +1)
    } else if (t < tApproach + tFlash) {
      const fadeFlash = 1 - (t - tApproach) / tFlash
      drawFlash(ctx, cx, cy, alpha, beta, fadeFlash, 80)
    }
    // após o flash, pausa breve antes do reset

    // IP marker (sempre)
    const ip = project3D(0, 0, 0, alpha, beta)
    ctx.fillStyle = '#fff'
    ctx.beginPath(); ctx.arc(cx + ip.x, cy + ip.y, 2.5, 0, Math.PI * 2); ctx.fill()

    // leitor do estado inicial
    ctx.fillStyle = 'rgba(255,255,255,0.5)'
    ctx.font = "600 9px 'JetBrains Mono',monospace"
    ctx.textAlign = 'left'
    ctx.fillText('ESTADO INICIAL', ox + 14, oy + 30)
    ctx.fillStyle = PZ_COLOR
    ctx.fillText('Σ p_z ≈ 0', ox + 14, oy + 48)
    ctx.fillStyle = PT_COLOR
    ctx.fillText('Σ p_T  = 0', ox + 14, oy + 62)
  }

  // próton com vetor p_z
  function drawProton(ctx: CanvasRenderingContext2D, cx: number, cy: number, alpha: number, beta: number, x: number, y: number, z: number, signZ: number) {
    const p = project3D(x, y, z, alpha, beta)
    // esfera
    ctx.fillStyle = '#fff'
    ctx.shadowColor = 'rgba(255,255,255,0.6)'
    ctx.shadowBlur = 10
    ctx.beginPath(); ctx.arc(cx + p.x, cy + p.y, 6, 0, Math.PI * 2); ctx.fill()
    ctx.shadowBlur = 0
    // vetor p_z
    const lenZ = 45 * signZ
    const pe = project3D(x, y, z + lenZ, alpha, beta)
    drawArrow(ctx, cx + p.x, cy + p.y, cx + pe.x, cy + pe.y, PZ_COLOR, 2.5)
    // rótulo
    ctx.fillStyle = PZ_COLOR
    ctx.font = "italic 700 11px 'Inter Tight',sans-serif"
    ctx.textAlign = 'left'
    ctx.fillText('p_z', cx + pe.x + 5, cy + pe.y)
  }

  // flash de colisão
  function drawFlash(ctx: CanvasRenderingContext2D, cx: number, cy: number, alpha: number, beta: number, intensity: number, maxRadius: number) {
    const ip = project3D(0, 0, 0, alpha, beta)
    const r = (1 - intensity) * maxRadius + 8
    // núcleo branco
    ctx.globalAlpha = intensity
    ctx.fillStyle = '#fff'
    ctx.beginPath(); ctx.arc(cx + ip.x, cy + ip.y, r * 0.4, 0, Math.PI * 2); ctx.fill()
    // halo
    ctx.globalAlpha = intensity * 0.4
    ctx.beginPath(); ctx.arc(cx + ip.x, cy + ip.y, r, 0, Math.PI * 2); ctx.fill()
    ctx.globalAlpha = 1
  }

  // === Fase 2: a colisão (várias partículas emergindo) ===
  function drawCollisionScene(ctx: CanvasRenderingContext2D, ox: number, oy: number, w: number, h: number) {
    const cx = ox + w / 2, cy = oy + h / 2
    const alpha = camRef.current.el
    const beta = camRef.current.az

    drawDetectorWireframe(ctx, cx, cy, alpha, beta)
    drawAxisGizmo(ctx, ox + 50, oy + h - 50, alpha, beta)

    // ciclo: flash → partículas voando → pausa → reset
    const T = 5.5
    const t = tRef.current % T
    const tFlash = 0.4
    const tFly = 4.5

    // flash inicial
    if (t < tFlash) {
      drawFlash(ctx, cx, cy, alpha, beta, 1 - t / tFlash, 100)
    }

    // partículas
    if (t > 0.05 && t < tFly) {
      const tSince = t - 0.05 // começa um pouco depois do flash inicial
      for (const part of COLLISION_PARTS) {
        const th = part.theta * Math.PI / 180
        const phi = part.phi * Math.PI / 180
        const dx = Math.sin(th) * Math.cos(phi)
        const dy = Math.sin(th) * Math.sin(phi)
        const dz = Math.cos(th)
        const distSpeed = 65 // px/s
        const dist = distSpeed * tSince
        // trail
        const segs = 30
        for (let i = 0; i < segs; i++) {
          const f1 = i / segs, f2 = (i + 1) / segs
          const d1 = Math.max(0, dist * f1)
          const d2 = Math.max(0, dist * f2)
          if (d2 <= d1) continue
          const p1 = project3D(d1 * dx, d1 * dy, d1 * dz, alpha, beta)
          const p2 = project3D(d2 * dx, d2 * dy, d2 * dz, alpha, beta)
          ctx.strokeStyle = part.color
          ctx.globalAlpha = Math.pow(f1, 0.8)
          ctx.lineWidth = part.highlighted ? 2.5 : 1.5
          if (part.dashed) ctx.setLineDash([3, 3])
          ctx.beginPath()
          ctx.moveTo(cx + p1.x, cy + p1.y)
          ctx.lineTo(cx + p2.x, cy + p2.y)
          ctx.stroke()
          ctx.setLineDash([])
        }
        ctx.globalAlpha = 1
        // cabeça
        const pHead = project3D(dist * dx, dist * dy, dist * dz, alpha, beta)
        if (!part.dashed) {
          ctx.fillStyle = part.color
          ctx.shadowColor = part.color
          ctx.shadowBlur = part.highlighted ? 14 : 5
          ctx.beginPath()
          ctx.arc(cx + pHead.x, cy + pHead.y, part.highlighted ? 5.5 : 3, 0, Math.PI * 2)
          ctx.fill()
          ctx.shadowBlur = 0
        }
        // anel pulsante na destacada
        if (part.highlighted) {
          const pulse = 1 + 0.35 * Math.sin(tRef.current * 6)
          ctx.strokeStyle = part.color
          ctx.lineWidth = 1.5
          ctx.globalAlpha = 0.55
          ctx.beginPath()
          ctx.arc(cx + pHead.x, cy + pHead.y, 11 * pulse, 0, Math.PI * 2)
          ctx.stroke()
          ctx.globalAlpha = 1
        }
        // rótulo após distância mínima
        if (dist > 35) {
          ctx.fillStyle = part.color
          ctx.globalAlpha = part.dashed ? 0.7 : 1
          ctx.font = part.highlighted
            ? "italic 700 13px 'Inter Tight',sans-serif"
            : "italic 600 11px 'Inter Tight',sans-serif"
          ctx.textAlign = 'left'
          ctx.fillText(part.label, cx + pHead.x + 8, cy + pHead.y - 4)
          ctx.globalAlpha = 1
        }
      }
    }

    // IP
    const ip = project3D(0, 0, 0, alpha, beta)
    ctx.fillStyle = '#fff'
    ctx.beginPath(); ctx.arc(cx + ip.x, cy + ip.y, 2.5, 0, Math.PI * 2); ctx.fill()

    // legenda
    ctx.fillStyle = 'rgba(255,255,255,0.5)'
    ctx.font = "600 9px 'JetBrains Mono',monospace"
    ctx.textAlign = 'left'
    ctx.fillText('PARTÍCULAS FINAIS', ox + 14, oy + 30)
    ctx.fillStyle = 'rgba(221,221,0,0.7)'
    ctx.fillText('γ  fóton (sem massa)', ox + 14, oy + 48)
    ctx.fillStyle = 'rgba(140,140,140,0.7)'
    ctx.fillText('ν  neutrino (escapa)', ox + 14, oy + 62)
    ctx.fillStyle = NEG_COLOR
    ctx.fillText('μ⁻  vamos seguir →', ox + 14, oy + 80)
  }

  // === painel transverso ===
  function drawTransversePanel(ctx: CanvasRenderingContext2D, ox: number, oy: number, w: number, h: number) {
    const cx = ox + w / 2, cy = oy + h / 2

    // título do painel
    ctx.fillStyle = 'rgba(255,255,255,0.35)'
    ctx.font = "600 10px 'JetBrains Mono',monospace"
    ctx.textAlign = 'center'
    ctx.fillText('VISTA TRANSVERSA (r-φ)', cx, oy + 18)

    // grade polar sutil — apenas referência de escala, sem implicar camadas físicas
    ctx.strokeStyle = '#1a1a1a'
    ctx.lineWidth = 0.6
    for (const r of [40, 80, 120]) {
      ctx.beginPath()
      ctx.arc(cx, cy, r, 0, Math.PI * 2)
      ctx.stroke()
    }
    // raios cardinais
    for (let i = 0; i < 4; i++) {
      const a = i * Math.PI / 2
      ctx.beginPath()
      ctx.moveTo(cx + 6 * Math.cos(a), cy + 6 * Math.sin(a))
      ctx.lineTo(cx + 130 * Math.cos(a), cy + 130 * Math.sin(a))
      ctx.stroke()
    }

    // símbolo de B saindo da tela
    ctx.strokeStyle = B_COLOR
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.arc(cx, cy, 2, 0, Math.PI * 2)
    ctx.fill()
    ctx.fillStyle = B_COLOR
    ctx.fillText('⊙ B', cx + 40, cy - 100)

    // círculo de trajetória
    const useR = Math.min(R_px * 0.45, 110) // reescalar para caber
    const angleSign = charge > 0 ? 1 : -1
    // mesma lógica de ciclo da vista 3D, mas usando ângulo em vez de fração de detector
    const omega2D = 2 * (B > 0.1 ? Math.max(B / 2.0, 0.3) : 1)
    const sMax = (tRef.current * omega2D) % (8 * Math.PI) // ciclo a cada 4 voltas para o trail respirar
    const trailLen = Math.min(5, sMax)
    for (let i = 0; i < 100; i++) {
      const f1 = i / 100, f2 = (i + 1) / 100
      const s1 = sMax - trailLen * (1 - f1)
      const s2 = sMax - trailLen * (1 - f2)
      ctx.strokeStyle = charge > 0 ? POS_COLOR : NEG_COLOR
      ctx.globalAlpha = Math.pow(f1, 1.6)
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.moveTo(cx + useR * Math.cos(s1 * angleSign), cy + useR * Math.sin(s1 * angleSign))
      ctx.lineTo(cx + useR * Math.cos(s2 * angleSign), cy + useR * Math.sin(s2 * angleSign))
      ctx.stroke()
    }
    ctx.globalAlpha = 1
    // partícula
    const partColor = charge > 0 ? POS_COLOR : NEG_COLOR
    ctx.fillStyle = partColor
    ctx.shadowColor = partColor
    ctx.shadowBlur = 10
    ctx.beginPath()
    ctx.arc(cx + useR * Math.cos(sMax * angleSign), cy + useR * Math.sin(sMax * angleSign), 5, 0, Math.PI * 2)
    ctx.fill()
    ctx.shadowBlur = 0

    // raio cotado
    ctx.strokeStyle = 'rgba(242, 166, 35, 0.5)'
    ctx.setLineDash([4, 3])
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(cx, cy)
    ctx.lineTo(cx + useR * Math.cos(sMax * angleSign), cy + useR * Math.sin(sMax * angleSign))
    ctx.stroke()
    ctx.setLineDash([])
    // rótulo R
    ctx.fillStyle = PT_COLOR
    ctx.font = "italic 700 12px 'Inter Tight',sans-serif"
    const midX = cx + (useR / 2) * Math.cos(sMax * angleSign)
    const midY = cy + (useR / 2) * Math.sin(sMax * angleSign)
    ctx.fillText('R', midX + 6, midY - 6)

    // IP
    ctx.fillStyle = '#fff'
    ctx.beginPath()
    ctx.arc(cx, cy, 2.5, 0, Math.PI * 2)
    ctx.fill()

    // leitor numérico
    const yReader = oy + h - 60
    ctx.fillStyle = 'rgba(255,255,255,0.5)'
    ctx.font = "600 9px 'JetBrains Mono',monospace"
    ctx.textAlign = 'left'
    ctx.fillText(`R   = ${R_m.toFixed(1)} m`, ox + 14, yReader)
    ctx.fillText(`p_T = ${pT.toFixed(1)} GeV/c`, ox + 14, yReader + 14)
    ctx.fillText(`p_z = ${pZ.toFixed(1)} GeV/c`, ox + 14, yReader + 28)
    ctx.fillText(`B   = ${B.toFixed(2)} T`, ox + 14, yReader + 42)

    // fórmula
    ctx.fillStyle = PT_COLOR
    ctx.font = "italic 600 11px 'Inter Tight',sans-serif"
    ctx.fillText('p_T = 0,3 · B · R', ox + 14, oy + 38)
  }

  // === cena MET (fase 5) ===
  function drawMETScene(ctx: CanvasRenderingContext2D) {
    const cx = CW / 2, cy = CH / 2

    // título
    ctx.fillStyle = 'rgba(255,255,255,0.35)'
    ctx.font = "600 10px 'JetBrains Mono',monospace"
    ctx.textAlign = 'center'
    ctx.fillText('VISTA TRANSVERSA — SOMA DE p_T', cx, 22)

    // grade polar sutil — apenas referência de escala radial
    ctx.strokeStyle = '#1a1a1a'
    ctx.lineWidth = 0.6
    for (const r of [50, 100, 150]) {
      ctx.beginPath()
      ctx.arc(cx, cy, r, 0, Math.PI * 2)
      ctx.stroke()
    }
    for (let i = 0; i < 4; i++) {
      const a = i * Math.PI / 2
      ctx.beginPath()
      ctx.moveTo(cx + 8 * Math.cos(a), cy + 8 * Math.sin(a))
      ctx.lineTo(cx + 160 * Math.cos(a), cy + 160 * Math.sin(a))
      ctx.stroke()
    }

    // quatro partículas finais saindo do vértice
    const partAngles = [Math.PI * 0.3, Math.PI * 0.95, Math.PI * 1.55, Math.PI * 1.85]
    const partMags = [70, 85, 60, 75]
    const partLabels = ['μ⁺', 'μ⁻', 'e⁻', 'ν']
    const partColors = [POS_COLOR, NEG_COLOR, NEG_COLOR, '#666']

    // soma vetorial: polígono cabeça-a-cauda
    const cycle = (tRef.current % 6) / 6
    const nVis = Math.floor(cycle * 4) + 1

    const visiblePart = withNeutrino ? 4 : 3

    for (let i = 0; i < Math.min(nVis, visiblePart); i++) {
      const a = partAngles[i], m = partMags[i]
      // traço saindo do vértice
      ctx.strokeStyle = partColors[i]
      ctx.lineWidth = 2
      if (i === 3 && !withNeutrino) {
        // invisível: tracejado cinza
        ctx.setLineDash([4, 4])
        ctx.strokeStyle = '#444'
      }
      ctx.beginPath()
      ctx.moveTo(cx, cy)
      ctx.lineTo(cx + m * Math.cos(a), cy + m * Math.sin(a))
      ctx.stroke()
      ctx.setLineDash([])

      // rótulo
      ctx.fillStyle = partColors[i]
      ctx.font = "italic 600 12px 'Inter Tight',sans-serif"
      ctx.textAlign = 'left'
      ctx.fillText(partLabels[i], cx + (m + 10) * Math.cos(a), cy + (m + 10) * Math.sin(a) + 4)
    }

    // polígono de momento na esquerda
    const pX = 60, pY = CH - 80
    ctx.fillStyle = 'rgba(255,255,255,0.5)'
    ctx.font = "600 9px 'JetBrains Mono',monospace"
    ctx.textAlign = 'left'
    ctx.fillText('SOMA DE p_T', pX, pY - 60)

    let curX = pX, curY = pY
    for (let i = 0; i < Math.min(nVis, visiblePart); i++) {
      const a = partAngles[i], m = partMags[i] * 0.5
      const nx = curX + m * Math.cos(a)
      const ny = curY + m * Math.sin(a)
      drawArrow(ctx, curX, curY, nx, ny, partColors[i], 1.8)
      curX = nx; curY = ny
    }
    // se faltou neutrino, mostrar gap até origem em vermelho
    if (!withNeutrino && nVis >= 3) {
      const dx = pX - curX, dy = pY - curY
      const len = Math.sqrt(dx * dx + dy * dy)
      if (len > 2) {
        ctx.setLineDash([3, 3])
        drawArrow(ctx, curX, curY, pX, pY, HC.met, 2)
        ctx.setLineDash([])
        ctx.fillStyle = HC.met
        ctx.font = "italic 700 13px 'Inter Tight',sans-serif"
        ctx.fillText('MET', (curX + pX) / 2 + 8, (curY + pY) / 2)
      }
    }

    // status
    ctx.fillStyle = withNeutrino ? SUCCESS : HC.met
    ctx.font = "600 11px 'Inter Tight',sans-serif"
    ctx.textAlign = 'right'
    ctx.fillText(
      withNeutrino ? '✓ Polígono FECHA — sem MET' : '✗ Polígono NÃO FECHA — há MET',
      CW - 14, CH - 18
    )

    // IP
    ctx.fillStyle = '#fff'
    ctx.beginPath()
    ctx.arc(cx, cy, 3, 0, Math.PI * 2)
    ctx.fill()
  }

  // === controles visíveis por fase ===
  // Fases pós-colisão acompanhando uma partícula: 3 (sem campo), 4 (com campo), 5 (projeção)
  // Fase 6: MET
  const showChargeCtrl = phase >= 3 && phase <= 5
  const showBCtrl = phase >= 3 && phase <= 5
  const showThetaCtrl = phase >= 3 && phase <= 5
  const showPCtrl = phase === 5
  const showMETCtrl = phase === 6

  return (
    <div className="atlas-lab">
      <div className="atlas-lab-container">
        {/* === HEADER === */}
        <div className="atlas-lab-title-block">
          <div className="atlas-lab-step">Masterclass IPPOG — Passo 3</div>
          <h1 className="atlas-lab-title">Momento transverso no <span>campo magnético</span></h1>
          <p className="atlas-lab-subtitle">
            Da colisão dos prótons à soma vetorial dos momentos finais
          </p>
        </div>

        {/* === PÍLULAS === */}
        <div className="atlas-sig-picker">
          {PHASES.map(P => {
            const active = phase === P.id
            const done = visited[P.id]
            const pillAccent = P.color === '#fff' ? '#3a6a9f' : P.color
            return (
              <button
                key={P.id}
                type="button"
                className={'atlas-sig-pill' + (done ? ' is-done' : '')}
                disabled={readOnly}
                onClick={() => { if (readOnly) return; goPhase(P.id, 'click', 'self'); onLayerFocused?.('phase-' + P.id) }}
                style={active ? { borderColor: pillAccent, background: pillAccent + '18' } : undefined}
              >
                <span className="atlas-sig-pill-sym" style={{ color: pillAccent }}>{P.id + 1}</span>
                <span className="atlas-sig-pill-label">{P.tag}</span>
              </button>
            )
          })}
        </div>

        {/* === CANVAS === */}
        <div style={{ position: 'relative', maxWidth: CW, margin: '0 auto 10px' }}>
          <div className="atlas-lab-canvas-frame" style={{ aspectRatio: `${CW} / ${CH}` }}>
            <canvas
              ref={cvRef}
              className="atlas-lab-canvas"
              onMouseDown={onPointerDown}
              onMouseMove={onPointerMove}
              onMouseUp={onPointerUp}
              onMouseLeave={onPointerUp}
              onTouchStart={onPointerDown}
              onTouchMove={onPointerMove}
              onTouchEnd={onPointerUp}
              onTouchCancel={onPointerUp}
              style={{
                cursor: phase === 6 ? 'default' : isDragging ? 'grabbing' : 'grab',
                touchAction: phase === 6 ? 'auto' : 'none',
                userSelect: 'none'
              }}
            />
          </div>
          {/* botão auto-rotação (escondido na fase 6 que é 2D) */}
          {phase !== 6 && (
            <button
              onClick={() => { if (readOnly) return; setAutoRotate(a => { const next = !a; onMissionEventRef.current?.('controlChanged', { control: 'autoRotate', value: next, phase, clientTs: Date.now() }); return next }) }}
              style={{
                position: 'absolute', top: 10, right: 10,
                padding: '5px 9px', borderRadius: 6,
                background: autoRotate ? B_COLOR + '25' : '#0d0d0d',
                border: `1px solid ${autoRotate ? B_COLOR + '60' : '#2a2a2a'}`,
                color: autoRotate ? B_COLOR : '#666',
                fontSize: '.58em', fontWeight: 700, cursor: 'pointer',
                fontFamily: 'JetBrains Mono,monospace',
                textTransform: 'uppercase', letterSpacing: 1,
                display: 'flex', alignItems: 'center', gap: 4
              }}
              title={autoRotate ? 'Pausar rotação automática' : 'Retomar rotação automática'}
            >
              <span style={{ fontSize: '1.1em', lineHeight: 1 }}>{autoRotate ? '⏸' : '↻'}</span>
              <span>{autoRotate ? 'Auto' : 'Manual'}</span>
            </button>
          )}
          {/* hint de interação na primeira fase com 3D ativo */}
          {phase !== 6 && !isDragging && (
            <div style={{
              position: 'absolute', bottom: 10, left: 10,
              padding: '4px 8px', borderRadius: 5,
              background: 'rgba(0,0,0,0.55)',
              color: 'rgba(255,255,255,0.45)',
              fontSize: '.55em', fontWeight: 600,
              fontFamily: 'JetBrains Mono,monospace',
              letterSpacing: 0.5, pointerEvents: 'none'
            }}>
              arraste para girar a câmera
            </div>
          )}
        </div>

        {/* === CONTROLES === */}
        {(showChargeCtrl || showBCtrl || showThetaCtrl || showPCtrl || showMETCtrl) && (
          <div style={{
            padding: '10px 14px', borderRadius: 10,
            background: '#ffffff', border: '1px solid #e2e0d8',
            marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 8
          }}>
            {showChargeCtrl && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: '.62em', color: '#555550', fontWeight: 700, minWidth: 70, fontFamily: 'JetBrains Mono,monospace', textTransform: 'uppercase', letterSpacing: 1 }}>Carga</span>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button onClick={() => { if (readOnly) return; setCharge(+1); onMissionEventRef.current?.('controlChanged', { control: 'charge', value: +1, phase, clientTs: Date.now() }) }} disabled={readOnly} style={{
                    padding: '4px 12px', borderRadius: 6,
                    background: charge > 0 ? POS_COLOR + '18' : '#ffffff',
                    border: `1.5px solid ${charge > 0 ? POS_COLOR : '#e2e0d8'}`,
                    color: charge > 0 ? POS_COLOR : '#555550',
                    fontSize: '.7em', fontWeight: 700, cursor: readOnly ? 'default' : 'pointer',
                    opacity: readOnly ? 0.5 : 1,
                    fontFamily: 'JetBrains Mono,monospace'
                  }}>+</button>
                  <button onClick={() => { if (readOnly) return; setCharge(-1); onMissionEventRef.current?.('controlChanged', { control: 'charge', value: -1, phase, clientTs: Date.now() }) }} disabled={readOnly} style={{
                    padding: '4px 12px', borderRadius: 6,
                    background: charge < 0 ? NEG_COLOR + '18' : '#ffffff',
                    border: `1.5px solid ${charge < 0 ? NEG_COLOR : '#e2e0d8'}`,
                    color: charge < 0 ? NEG_COLOR : '#555550',
                    fontSize: '.7em', fontWeight: 700, cursor: readOnly ? 'default' : 'pointer',
                    opacity: readOnly ? 0.5 : 1,
                    fontFamily: 'JetBrains Mono,monospace'
                  }}>−</button>
                </div>
              </div>
            )}
            {showBCtrl && (
              <SliderRow
                label="|B|"
                value={B}
                unit="T"
                min={0}
                max={2.5}
                step={0.05}
                color={B_COLOR}
                disabled={readOnly}
                onChange={(v) => { if (readOnly) return; setB(v); onMissionEventRef.current?.('controlChanged', { control: 'B', value: v, phase, clientTs: Date.now() }) }}
              />
            )}
            {showThetaCtrl && (
              <SliderRow
                label="θ"
                value={theta}
                unit="°"
                min={5}
                max={89}
                step={1}
                color="#3a6a9f"
                disabled={readOnly}
                onChange={(v) => { if (readOnly) return; setTheta(v); onMissionEventRef.current?.('controlChanged', { control: 'theta', value: v, phase, clientTs: Date.now() }) }}
              />
            )}
            {showPCtrl && (
              <SliderRow
                label="|p|"
                value={pTotal}
                unit="GeV/c"
                min={2}
                max={50}
                step={0.5}
                color={PT_COLOR}
                disabled={readOnly}
                onChange={(v) => { if (readOnly) return; setPTotal(v); onMissionEventRef.current?.('controlChanged', { control: 'pTotal', value: v, phase, clientTs: Date.now() }) }}
              />
            )}
            {showMETCtrl && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: '.62em', color: '#555550', fontWeight: 700, minWidth: 70, fontFamily: 'JetBrains Mono,monospace', textTransform: 'uppercase', letterSpacing: 1 }}>Neutrino</span>
                <button onClick={() => { if (readOnly) return; setWithNeutrino(v => { const next = !v; onMissionEventRef.current?.('controlChanged', { control: 'neutrino', value: next, phase, clientTs: Date.now() }); return next }) }} disabled={readOnly} style={{
                  padding: '4px 14px', borderRadius: 6,
                  background: withNeutrino ? SUCCESS + '18' : HC.met + '14',
                  border: `1.5px solid ${withNeutrino ? '#2d7a1e' : HC.met}`,
                  color: withNeutrino ? '#2d7a1e' : HC.met,
                  fontSize: '.68em', fontWeight: 700, cursor: readOnly ? 'default' : 'pointer',
                  opacity: readOnly ? 0.5 : 1,
                  fontFamily: 'JetBrains Mono,monospace', textTransform: 'uppercase', letterSpacing: 1
                }}>{withNeutrino ? 'Incluído' : 'Escapou'}</button>
              </div>
            )}
          </div>
        )}

        {/* === CARD === */}
        <article className="atlas-lab-card" style={{ marginBottom: 10 }}>
          <div className="atlas-lab-card-strip">
            <div className="atlas-lab-card-strip-row">
              <div className="atlas-lab-card-icon" style={{ borderColor: cardAccent, background: cardAccent + '20', color: cardAccent, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>
                {phase + 1}
              </div>
              <div className="atlas-lab-card-strip-text">
                <div className="atlas-lab-card-label">{ph.title}</div>
                <div className="atlas-lab-card-subtitle" style={{ color: cardAccent }}>{ph.tag}</div>
              </div>
            </div>
          </div>
          <div className="atlas-lab-card-body">
            <div className="atlas-lab-card-section">
              <div className="atlas-lab-card-kicker" style={{ color: cardAccent }}>
                <span className="atlas-lab-card-kicker-bar" style={{ background: cardAccent }} />
                O que é
              </div>
              <p className="atlas-lab-card-text">{ph.what}</p>
            </div>
            <div className="atlas-lab-card-divider" />
            <div className="atlas-lab-card-section">
              <div className="atlas-lab-card-kicker" style={{ color: cardAccent }}>
                <span className="atlas-lab-card-kicker-bar" style={{ background: cardAccent }} />
                Para que serve
              </div>
              <p className="atlas-lab-card-text">{ph.role}</p>
            </div>
            <div className="atlas-lab-card-divider" />
            <div className="atlas-lab-card-section">
              <div className="atlas-lab-card-kicker" style={{ color: cardAccent }}>
                <span className="atlas-lab-card-kicker-bar" style={{ background: cardAccent }} />
                Dica de observação
              </div>
              <p className="atlas-lab-card-text is-muted">{ph.detail}</p>
            </div>
          </div>
        </article>

        {/* === NAVEGAÇÃO === */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginBottom: 10 }}>
          <button
            onClick={() => { if (readOnly) return; const i = Math.max(0, phase - 1); goPhase(i, 'click', 'self'); onLayerFocused?.('phase-' + i) }}
            disabled={phase === 0}
            style={{
              padding: '8px 14px', borderRadius: 8,
              background: '#ffffff',
              border: `1.5px solid ${phase === 0 ? '#ebe9e1' : '#e2e0d8'}`,
              color: phase === 0 ? '#bbb8ae' : '#555550',
              fontSize: '.7em', fontWeight: 700, cursor: phase === 0 ? 'default' : 'pointer',
              fontFamily: 'JetBrains Mono,monospace', letterSpacing: 1
            }}>← ANTERIOR</button>
          <button
            onClick={() => { if (readOnly) return; const i = Math.min(PHASES.length - 1, phase + 1); goPhase(i, 'click', 'self'); onLayerFocused?.('phase-' + i) }}
            disabled={phase === PHASES.length - 1}
            style={{
              padding: '8px 14px', borderRadius: 8,
              background: phase === PHASES.length - 1 ? '#ffffff' : cardAccent + '18',
              border: `1.5px solid ${phase === PHASES.length - 1 ? '#ebe9e1' : cardAccent}`,
              color: phase === PHASES.length - 1 ? '#bbb8ae' : cardAccent,
              fontSize: '.7em', fontWeight: 700,
              cursor: phase === PHASES.length - 1 ? 'default' : 'pointer',
              fontFamily: 'JetBrains Mono,monospace', letterSpacing: 1
            }}>PRÓXIMA →</button>
        </div>

        {/* === COMPLETION === */}
        {allVisited && (
          <div style={{
            marginTop: 10, padding: '12px 16px', borderRadius: 12,
            background: '#f0fae8', border: '1.5px solid #b8e0a0',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '.85em', fontWeight: 700, color: '#2d7a1e', marginBottom: 4 }}>
              Sete fases concluídas!
            </div>
            <p style={{ fontSize: '.72em', color: '#555550', margin: 0 }}>
              Você percorreu o caminho do estado inicial dos prótons até a soma vetorial dos momentos finais.
              Agora sabe por que toda variável "interessante" no ATLAS leva o subscrito T.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default HypatiaMomentoTransverso

// === subcomponentes ===
interface SliderRowProps {
  label: string
  value: number
  unit: string
  min: number
  max: number
  step: number
  color: string
  onChange: (value: number) => void
  disabled?: boolean
}

function SliderRow({ label, value, unit, min, max, step, color, onChange, disabled = false }: SliderRowProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, opacity: disabled ? 0.5 : 1 }}>
      <span style={{ fontSize: '.62em', color: '#555550', fontWeight: 700, minWidth: 70, fontFamily: 'JetBrains Mono,monospace', textTransform: 'uppercase', letterSpacing: 1 }}>{label}</span>
      <input
        type="range"
        min={min} max={max} step={step} value={value}
        disabled={disabled}
        onChange={e => onChange(parseFloat(e.target.value))}
        style={{ flex: 1, accentColor: color }}
      />
      <span style={{ fontSize: '.7em', color, fontWeight: 700, minWidth: 80, textAlign: 'right', fontFamily: 'JetBrains Mono,monospace' }}>
        {value.toFixed(value % 1 === 0 ? 0 : 2)} {unit}
      </span>
    </div>
  )
}
